import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ClipboardList, Lock, Search } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DateRangeColumnFilter, MultiSelectColumnFilter } from "@/components/shared/column-filter";
import { type ColumnDefinition, ColumnManager } from "@/components/shared/column-manager";
import { ExportButton } from "@/components/shared/export-button";
import { NumberHoverCard } from "@/components/shared/number-hover-card";
import { type BreadcrumbTrailItem, PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { QuickFilterChip } from "@/components/shared/quick-filter-chip";
import { nextSortState, SortableHeader, type SortDir } from "@/components/shared/sortable-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SectionBar } from "@/components/ui/section-bar";
import { StatusBadge } from "@/components/ui/status-badge";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import type { ClaimsMrpRow } from "@/hooks/use-claim";
import { useClaimsMrp, useClaimsMrpFacets } from "@/hooks/use-claim";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useSessionState } from "@/hooks/use-session-state";
import { useTablePreferences } from "@/hooks/use-table-preferences";
import { encodeFromAgency, encodeFromPolicy } from "@/lib/breadcrumb-from";
import { isOpenStatus } from "@/lib/claim-status";
import type { ExportColumn } from "@/lib/export";
import { formatCurrency, formatDate, toLocalIsoDate } from "@/lib/format";
import { claimStatusKey } from "@/lib/status-colors";
import { claimsMrpSearchSchema } from "@/schemas/search";

type ClaimsMrpFilters = {
  policyNumber?: string;
  agentNumber?: string;
  isOpenIndemnity?: string;
  isOpenMedicalOnly?: string;
  isClaimOpen?: string;
  lossType?: string;
};

type SecondaryColumnKey =
  | "claimant"
  | "currentStatus"
  | "lossTypeDescription"
  | "lossDate"
  | "accidentState"
  | "adjusterName"
  | "incurred"
  | "reserve"
  | "paid";

const CLAIMS_MRP_DEFAULT_ORDER: readonly SecondaryColumnKey[] = [
  "claimant",
  "currentStatus",
  "lossTypeDescription",
  "lossDate",
  "accidentState",
  "adjusterName",
  "incurred",
  "reserve",
  "paid",
];

const CLAIMS_MRP_COLUMN_LABELS: Record<SecondaryColumnKey, string> = {
  claimant: "Claimant",
  currentStatus: "Status",
  lossTypeDescription: "Loss Type",
  lossDate: "Loss Date",
  accidentState: "State",
  adjusterName: "Adjuster",
  incurred: "Incurred",
  reserve: "Reserve",
  paid: "Paid",
};

const CLAIMS_MRP_COLUMN_DEFS: readonly ColumnDefinition<SecondaryColumnKey>[] = (
  Object.keys(CLAIMS_MRP_COLUMN_LABELS) as SecondaryColumnKey[]
).map((key) => ({ key, label: CLAIMS_MRP_COLUMN_LABELS[key] }));

type SortKey =
  | "claimNumber"
  | "claimant"
  | "currentStatus"
  | "lossTypeDescription"
  | "lossDate"
  | "accidentState"
  | "adjusterName"
  | "incurred"
  | "reserve"
  | "paid";

function filterTitle(filters: ClaimsMrpFilters): string {
  // Status piece: collect every active status flag so the title reflects
  // the full filter intent. Indemnity + Medical-Only combine to
  // "Open Indemnity & Medical-Only Claims"; either combined with
  // isClaimOpen is redundant (the loss-type flag is the more specific) so
  // we drop the bare "Open" piece in that case.
  const statuses: string[] = [];
  if (filters.isOpenIndemnity) statuses.push("Open Indemnity");
  if (filters.isOpenMedicalOnly) statuses.push("Open Medical-Only");
  // lossType composes with the bare Open filter: "Open Indemnity" when both
  // are set, "Indemnity" when only the loss type is. Falls through to the bare
  // "Open" piece only when no loss type is scoped.
  if (filters.lossType) {
    statuses.push(filters.isClaimOpen ? `Open ${filters.lossType}` : filters.lossType);
  } else if (filters.isClaimOpen && statuses.length === 0) {
    statuses.push("Open");
  }
  const status = statuses.length > 0 ? `${statuses.join(" & ")} Claims` : "All Claims";

  const scope = filters.policyNumber
    ? ` on Policy ${filters.policyNumber}`
    : filters.agentNumber
      ? ` on Agency ${filters.agentNumber}`
      : "";
  return `${status}${scope}`;
}

// Comparable claimant name for sort + filter. Falls back to "" for restricted
// rows so they sort to the top in asc order and don't leak the underlying
// name through ordering side-effects.
function claimantOf(c: {
  isRestricted: boolean;
  firstName: string | null;
  lastName: string | null;
}) {
  if (c.isRestricted) return "";
  return [c.firstName, c.lastName].filter(Boolean).join(" ");
}

export function ClaimsMrpPage() {
  // Re-parse with the same Zod schema the route's validateSearch uses. We
  // cannot use `useSearch({ from: "/claims/mrp" })` because that needs
  // TanStack Router typegen which this project doesn't have wired up.
  const search = claimsMrpSearchSchema.parse(useSearch({ strict: false }));
  const navigate = useNavigate();
  // Per-tab persistence (useSessionState) — leaving the table to drill into
  // a claim and hitting Back restores the same filters/sort the user left.
  // Filter text is intentionally not persisted; it's a transient lookup.
  const [filterText, setFilterText] = useState("");
  const debouncedFilter = useDebouncedValue(filterText, 200);
  const [sortKey, setSortKey] = useSessionState<SortKey>("c360:claims-mrp:sortKey", "claimNumber");
  const [sortDir, setSortDir] = useSessionState<SortDir>("c360:claims-mrp:sortDir", "asc");

  // Inline column filters — independent of the URL-driven status filters
  // (which scope the *API* query). These filter the loaded rows further on
  // the client. Empty array / empty object = no filter applied.
  const [statusFilter, setStatusFilter] = useSessionState<string[]>(
    "c360:claims-mrp:statusFilter",
    [],
  );
  const [lossTypeFilter, setLossTypeFilter] = useSessionState<string[]>(
    "c360:claims-mrp:lossTypeFilter",
    [],
  );
  const [stateFilter, setStateFilter] = useSessionState<string[]>(
    "c360:claims-mrp:stateFilter",
    [],
  );
  const [adjusterFilter, setAdjusterFilter] = useSessionState<string[]>(
    "c360:claims-mrp:adjusterFilter",
    [],
  );
  const [lossDateRange, setLossDateRange] = useSessionState<{ from?: string; to?: string }>(
    "c360:claims-mrp:lossDateRange",
    {},
  );
  // Quick-filter chip: "Last 30 days" is a client-side preset. The other
  // two chips (Open only, Indemnity only) are URL-driven so the API can
  // scope the result server-side and share the same flags the agency
  // dashboard sets when it deep-links here.
  const [last30Days, setLast30Days] = useSessionState<boolean>("c360:claims-mrp:last30Days", false);

  // Per-user column visibility + order. Claim Number is always first /
  // always visible and isn't in this list — it's hardcoded as the sticky
  // left column so the row never loses its identifier.
  const {
    orderedKeys: orderedSecondaryKeys,
    visibleKeys: visibleSecondaryKeys,
    isHidden: isSecondaryHidden,
    setHidden: setSecondaryHidden,
    setOrder: setSecondaryOrder,
    resetToDefaults: resetSecondaryColumns,
  } = useTablePreferences<SecondaryColumnKey>({
    tableId: "claims-mrp",
    defaultOrder: CLAIMS_MRP_DEFAULT_ORDER,
  });

  // Pin the sticky table header at the upper region's bottom edge.
  // negTop must equal main's pt-* (DashboardLayout) so the upper region's
  // pinned top reaches main's outer top edge — anything less leaves a
  // strip of padding above the bar that rows scroll through.
  const upperRef = useRef<HTMLDivElement>(null);
  const [headerTop, setHeaderTop] = useState<number>(132);
  useLayoutEffect(() => {
    const el = upperRef.current;
    if (!el) return;
    const mql = window.matchMedia("(min-width: 768px)");
    function update() {
      if (!el) return;
      const negTop = mql.matches ? 24 : 16;
      setHeaderTop(Math.ceil(el.getBoundingClientRect().height - negTop));
    }
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    mql.addEventListener("change", update);
    return () => {
      ro.disconnect();
      mql.removeEventListener("change", update);
    };
  }, []);

  const filters: ClaimsMrpFilters = {
    policyNumber: search.policyNumber,
    agentNumber: search.agentNumber,
    isOpenIndemnity: search.isOpenIndemnity,
    isOpenMedicalOnly: search.isOpenMedicalOnly,
    isClaimOpen: search.isClaimOpen,
    lossType: search.lossType,
  };

  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useClaimsMrp(filters);

  // Auto-load every page of the current server-scoped result. The per-column
  // filter menus (Status / Loss Type / State / Adjuster) and the client-side
  // refinement both read from loaded rows only — so without draining, the
  // funnel menus list just the values present on the first page and a filter
  // pick can't reach rows further down. Draining makes both complete, matching
  // how the Policy MRP already loads its full scoped set. POC-scale tradeoff:
  // a very large unscoped book should move to server-side facets + filtering
  // (Phase 10, when the SQL Server read path is built).
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allClaims = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const total = data?.pages[0]?.total;
  // Aggregates over the FULL server-filtered population, not just loaded
  // pages. Used as the source for the totals strip whenever the user
  // hasn't layered on a client-side refinement (column filter, free-text
  // search, or the Last-30-days chip). When they have, we fall back to
  // summing the loaded rows the user can actually see.
  const serverAggregates = data?.pages[0]?.aggregates;

  // The per-column dropdowns must offer every value a user could filter by —
  // the full domain in the data, not just what's on the current page (a list
  // showing only Open claims should still let you pick Closed). Those domain
  // lists come from the server facets endpoint. While they load, fall back to
  // the distinct values on the loaded rows so the menus aren't empty.
  const { data: facets } = useClaimsMrpFacets();
  const fallbackOptions = useMemo(() => {
    const status = new Set<string>();
    const lossType = new Set<string>();
    const state = new Set<string>();
    const adjuster = new Set<string>();
    for (const c of allClaims) {
      if (c.isRestricted) continue;
      if (c.currentStatus) status.add(c.currentStatus);
      if (c.lossTypeDescription) lossType.add(c.lossTypeDescription);
      if (c.accidentState) state.add(c.accidentState);
      if (c.adjusterName) adjuster.add(c.adjusterName);
    }
    const sortLocale = (a: string, b: string) => a.localeCompare(b);
    return {
      statusOptions: Array.from(status).sort(sortLocale),
      lossTypeOptions: Array.from(lossType).sort(sortLocale),
      stateOptions: Array.from(state).sort(sortLocale),
      adjusterOptions: Array.from(adjuster).sort(sortLocale),
    };
  }, [allClaims]);
  const statusOptions = facets?.statuses ?? fallbackOptions.statusOptions;
  const lossTypeOptions = facets?.lossTypes ?? fallbackOptions.lossTypeOptions;
  const stateOptions = facets?.states ?? fallbackOptions.stateOptions;
  const adjusterOptions = facets?.adjusters ?? fallbackOptions.adjusterOptions;

  // Filter + sort client-side over loaded rows. Restricted rows aren't
  // matched by the free-text filter (their personal fields read "Restricted"
  // for the user) — leaving them in the list when they don't match the
  // search would leak the existence of the search match through inclusion.
  // Inline column filters apply on top of the free-text filter.
  const visibleClaims = useMemo(() => {
    const q = debouncedFilter.trim().toLowerCase();
    const statusSet = new Set(statusFilter);
    const lossTypeSet = new Set(lossTypeFilter);
    const stateSet = new Set(stateFilter);
    const adjusterSet = new Set(adjusterFilter);
    const from = lossDateRange.from;
    const to = lossDateRange.to;
    // "Last 30 days" quick chip: derive a YYYY-MM-DD cutoff at render time
    // in the user's LOCAL timezone. Loss dates compare lexicographically
    // against the same shape — using toISOString here would silently drop a
    // day for Pacific-time users in the late evening.
    let last30From: string | undefined;
    if (last30Days) {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      last30From = toLocalIsoDate(d);
    }
    // Restricted rows are hidden whenever ANY filter is active — their
    // presence/absence in the result set would otherwise telegraph the
    // existence of the withheld claim.
    const anyFilterActive =
      q !== "" ||
      statusSet.size > 0 ||
      lossTypeSet.size > 0 ||
      stateSet.size > 0 ||
      adjusterSet.size > 0 ||
      Boolean(from) ||
      Boolean(to) ||
      last30Days;

    const filtered = allClaims.filter((c) => {
      if (c.isRestricted) return !anyFilterActive;

      if (q) {
        const hay = [
          c.claimNumber,
          claimantOf(c),
          c.currentStatus ?? "",
          c.lossTypeDescription ?? "",
          c.accidentState ?? "",
          c.adjusterName ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusSet.size > 0 && (!c.currentStatus || !statusSet.has(c.currentStatus))) return false;
      if (
        lossTypeSet.size > 0 &&
        (!c.lossTypeDescription || !lossTypeSet.has(c.lossTypeDescription))
      )
        return false;
      if (stateSet.size > 0 && (!c.accidentState || !stateSet.has(c.accidentState))) return false;
      if (adjusterSet.size > 0 && (!c.adjusterName || !adjusterSet.has(c.adjusterName)))
        return false;
      if (from && (!c.lossDate || c.lossDate < from)) return false;
      if (to && (!c.lossDate || c.lossDate > to)) return false;
      if (last30From && (!c.lossDate || c.lossDate < last30From)) return false;
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let cmp: number;
      switch (sortKey) {
        // Dollar columns sort numerically; the switch also narrows sortKey so
        // the string branches below never see a numeric field (no cast).
        case "incurred":
        case "reserve":
        case "paid":
          cmp = a[sortKey] - b[sortKey];
          break;
        case "claimant":
          cmp = claimantOf(a).localeCompare(claimantOf(b));
          break;
        case "claimNumber":
          cmp = a.claimNumber.localeCompare(b.claimNumber);
          break;
        case "lossDate":
          cmp = (a.lossDate ?? "").localeCompare(b.lossDate ?? "");
          break;
        default:
          cmp = (a[sortKey] ?? "").localeCompare(b[sortKey] ?? "");
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [
    allClaims,
    debouncedFilter,
    sortKey,
    sortDir,
    statusFilter,
    lossTypeFilter,
    stateFilter,
    adjusterFilter,
    lossDateRange.from,
    lossDateRange.to,
    last30Days,
  ]);

  // True when the user has narrowed the result set further on the client
  // (column filter, free-text search, or the Last-30-days chip). When false,
  // the totals strip can display server-side aggregates over the full
  // filtered population; when true, we sum the loaded rows the user can see.
  const hasClientRefinement =
    debouncedFilter.trim().length > 0 ||
    statusFilter.length > 0 ||
    lossTypeFilter.length > 0 ||
    stateFilter.length > 0 ||
    adjusterFilter.length > 0 ||
    Boolean(lossDateRange.from) ||
    Boolean(lossDateRange.to) ||
    last30Days;

  // Aggregate totals. Restriction only masks identifying fields (claim #,
  // claimant name, injury description) — status, loss-type, and dollars
  // pass through unchanged. Per the standing rule, totals are NEVER masked
  // or filtered: all restricted rows always feed every bucket, regardless
  // of the user's active filters.
  //
  // No client refinement → use server aggregates so the dollar/count totals
  // reflect the full filtered population, not just the currently-loaded
  // pages. Client refinement → fall back to summing loaded visible rows.
  const totals = useMemo(() => {
    if (!hasClientRefinement && serverAggregates) {
      return {
        incurred: serverAggregates.incurred,
        paid: serverAggregates.paid,
        reserve: serverAggregates.reserve,
        open: serverAggregates.openCount,
        closed: serverAggregates.closedCount,
        indemnity: serverAggregates.indemnityCount,
        medOnly: serverAggregates.medOnlyCount,
      };
    }
    let incurred = 0;
    let paid = 0;
    let reserve = 0;
    let open = 0;
    let closed = 0;
    let indemnity = 0;
    let medOnly = 0;
    const tally = (c: ClaimsMrpRow) => {
      incurred += c.incurred;
      paid += c.paid;
      reserve += c.reserve;
      if (isOpenStatus(c.currentStatus)) open += 1;
      else if (c.currentStatus) closed += 1;
      const lt = (c.lossTypeDescription ?? "").toLowerCase();
      if (lt.includes("med") && lt.includes("only")) medOnly += 1;
      else if (lt) indemnity += 1;
    };
    for (const c of visibleClaims) {
      if (c.isRestricted) continue;
      tally(c);
    }
    for (const c of allClaims) {
      if (!c.isRestricted) continue;
      tally(c);
    }
    return { incurred, paid, reserve, open, closed, indemnity, medOnly };
  }, [hasClientRefinement, serverAggregates, visibleClaims, allClaims]);

  // Columns the Export buttons emit. The shape matches what's visible on
  // screen so a CSV download reads like a copy of the table — restricted
  // rows mask claim-number/claimant the same way the UI does.
  const exportColumns = useMemo<ExportColumn<ClaimsMrpRow>[]>(
    () => [
      {
        key: "claimNumber",
        label: "Claim Number",
        value: (r) => (r.isRestricted ? "Restricted" : r.claimNumber),
      },
      {
        key: "claimant",
        label: "Claimant",
        value: (r) => (r.isRestricted ? "Restricted" : claimantOf(r)),
      },
      { key: "currentStatus", label: "Status", value: (r) => r.currentStatus },
      { key: "lossTypeDescription", label: "Loss Type", value: (r) => r.lossTypeDescription },
      { key: "lossDate", label: "Loss Date", value: (r) => r.lossDate },
      { key: "accidentState", label: "State", value: (r) => r.accidentState },
      { key: "adjusterName", label: "Adjuster", value: (r) => r.adjusterName },
      { key: "incurred", label: "Incurred", value: (r) => r.incurred },
      { key: "paid", label: "Paid", value: (r) => r.paid },
      { key: "reserve", label: "Reserve", value: (r) => r.reserve },
    ],
    [],
  );

  // Slug for the exported file. When the page is scoped (policy/agency),
  // include the identifier so a user with multiple downloads can tell them
  // apart at a glance.
  const exportFilename = filters.policyNumber
    ? `claims-policy-${filters.policyNumber}`
    : filters.agentNumber
      ? `claims-agency-${filters.agentNumber}`
      : "claims-mrp";

  function handleSort(key: SortKey) {
    const next = nextSortState({ key: sortKey, dir: sortDir }, key, {
      lossDate: "desc",
      incurred: "desc",
      reserve: "desc",
      paid: "desc",
    });
    setSortKey(next.key);
    setSortDir(next.dir);
  }

  const title = filterTitle(filters);
  // The totals strip flag fires only when we're actually summing a partial
  // view — i.e. the user has applied a client refinement AND there are more
  // server-side matches than have been loaded. Without client refinement
  // we display server aggregates that cover the full filtered population,
  // so no "loaded subset" warning is needed.
  const isLoadedSubset = hasClientRefinement && total !== undefined && allClaims.length < total;
  // Row-count label in the totals strip — server total when no client
  // refinement is in play, visible-row count otherwise.
  const stripCount = !hasClientRefinement && total !== undefined ? total : visibleClaims.length;
  const subtitle =
    total === undefined
      ? undefined
      : debouncedFilter
        ? `Showing ${visibleClaims.length} matching ${visibleClaims.length === 1 ? "claim" : "claims"}`
        : isLoadedSubset
          ? `Showing ${allClaims.length} of ${total.toLocaleString()} claims`
          : `${total.toLocaleString()} claim${total === 1 ? "" : "s"}`;

  const breadcrumbTrail: BreadcrumbTrailItem[] = [];
  if (filters.policyNumber && !filters.agentNumber) {
    breadcrumbTrail.push({
      key: "policy",
      link: (
        <Link to="/policy/$policyNumber" params={{ policyNumber: filters.policyNumber }}>
          Policy {filters.policyNumber}
        </Link>
      ),
    });
  } else if (filters.agentNumber && !filters.policyNumber) {
    breadcrumbTrail.push({
      key: "agency",
      link: (
        <Link to="/agency/$agentNumber" params={{ agentNumber: filters.agentNumber }}>
          Agency {filters.agentNumber}
        </Link>
      ),
    });
  }

  // Toggle a URL-driven filter on/off. Preserve the rest of the search
  // params so unrelated scoping (policyNumber / agentNumber) survives.
  function toggleUrlFilter(key: keyof ClaimsMrpFilters) {
    const next = { ...search };
    if (next[key]) {
      delete next[key];
    } else {
      next[key] = "true";
    }
    navigate({ to: "/claims/mrp", search: next });
  }

  // Toggle the loss-type scope on/off. Unlike isOpenIndemnity, this scopes by
  // loss type alone (any status), so the "Indemnity only" chip does what its
  // label says — combine it with "Open only" for open-indemnity.
  function toggleLossType(value: string) {
    const next = { ...search };
    if (next.lossType === value) {
      delete next.lossType;
    } else {
      next.lossType = value;
    }
    navigate({ to: "/claims/mrp", search: next });
  }

  const showSearch = allClaims.length > 0 || Boolean(debouncedFilter);
  // Right slot bundles the free-text search and the CSV/Excel export
  // dropdown so they share the SectionBar's right side. Both use the
  // "primary" tone styling so they sit cleanly on the brand-colored bar.
  const rightSlot =
    allClaims.length > 0 || debouncedFilter ? (
      <div className="flex items-center gap-2">
        {showSearch && (
          <div className="relative w-44 sm:w-56">
            <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 size-3.5 text-primary-foreground/60" />
            <Input
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Search..."
              className="h-7 border-primary-foreground/20 bg-primary-foreground/10 pl-8 text-primary-foreground text-xs placeholder:text-primary-foreground/60 focus-visible:ring-1 focus-visible:ring-primary-foreground/40"
            />
          </div>
        )}
        <ExportButton
          tone="primary"
          filename={exportFilename}
          columns={exportColumns}
          rows={visibleClaims}
        />
        <ColumnManager<SecondaryColumnKey>
          columns={CLAIMS_MRP_COLUMN_DEFS}
          orderedKeys={orderedSecondaryKeys}
          isHidden={isSecondaryHidden}
          tone="primary"
          onReorder={setSecondaryOrder}
          onToggleHidden={setSecondaryHidden}
          onReset={resetSecondaryColumns}
        />
      </div>
    ) : null;

  return (
    <div>
      {/* Sticky band over back button + panel header + totals strip.
          -top / -mt must equal main's pt-* (DashboardLayout) so the band
          fully covers main's padding zone — anything less leaks rows.
          pt-* is intentionally smaller than -top-* so the back button
          sits close to the app header while the bg covers the gap. */}
      <div
        ref={upperRef}
        style={{ backgroundColor: "var(--page-bg, var(--background))" }}
        className="-mx-4 -top-4 -mt-4 sticky z-30 space-y-2 px-4 pt-3 pb-0 md:-mx-6 md:-top-6 md:-mt-6 md:px-6 md:pt-4"
      >
        <PageBreadcrumbs trail={breadcrumbTrail} current="Claims" />

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-muted-foreground text-xs">Quick filters:</span>
          <QuickFilterChip
            label="Open only"
            description="Claims with status Open or Reopened."
            active={Boolean(filters.isClaimOpen)}
            onToggle={() => toggleUrlFilter("isClaimOpen")}
          />
          <QuickFilterChip
            label="Indemnity only"
            description="Claims with an indemnity loss type, at any status. Combine with Open only for open indemnity."
            active={filters.lossType === "Indemnity"}
            onToggle={() => toggleLossType("Indemnity")}
          />
          <QuickFilterChip
            label="Medical-Only"
            description="Claims with a medical-only loss type, at any status. Combine with Open only for open medical-only."
            active={filters.lossType === "Medical Only"}
            onToggle={() => toggleLossType("Medical Only")}
          />
          <QuickFilterChip
            label="Last 30 days"
            description="Claims with a loss date within the last 30 days."
            active={last30Days}
            onToggle={() => setLast30Days(!last30Days)}
          />
          {/* The policy summary card deep-links here with ?isOpenIndemnity=true
              / ?isOpenMedicalOnly=true (its "Open IN" / "Open MO" counts —
              open AND that loss type). Those are a distinct, combined scope
              from the loss-type chips above; show them as clearable chips only
              when present so the user can drop the deep-link filter without
              editing the URL. */}
          {filters.isOpenIndemnity && (
            <QuickFilterChip
              label="Open Indemnity"
              description="Open claims with an indemnity loss type."
              active
              onToggle={() => toggleUrlFilter("isOpenIndemnity")}
            />
          )}
          {filters.isOpenMedicalOnly && (
            <QuickFilterChip
              label="Open Medical-Only"
              description="Open claims with a medical-only loss type."
              active
              onToggle={() => toggleUrlFilter("isOpenMedicalOnly")}
            />
          )}
        </div>

        {/* Group opts SectionBar + totals strip out of the parent's space-y-2. */}
        <div>
          <SectionBar title={title} subtitle={subtitle} rightSlot={rightSlot} />
          {visibleClaims.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-x border-b bg-muted/60 px-3 py-1.5 text-muted-foreground text-xs">
              <span className="font-medium text-foreground">
                {stripCount.toLocaleString()} claim
                {stripCount === 1 ? "" : "s"}
              </span>
              {(totals.open > 0 || totals.closed > 0) && (
                <span>
                  Open <span className="font-medium text-foreground">{totals.open}</span> · Closed{" "}
                  <span className="font-medium text-foreground">{totals.closed}</span>
                </span>
              )}
              {(totals.indemnity > 0 || totals.medOnly > 0) && (
                <span>
                  Indemnity <span className="font-medium text-foreground">{totals.indemnity}</span>{" "}
                  · Med-Only <span className="font-medium text-foreground">{totals.medOnly}</span>
                </span>
              )}
              <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
                {isLoadedSubset && (
                  <span
                    className="text-muted-foreground italic"
                    title="More claims match this filter than have been loaded. Use the Load more button at the bottom of the table to bring in the rest."
                  >
                    loaded subset
                  </span>
                )}
                <span>
                  Incurred{" "}
                  <span className="font-medium text-foreground">
                    {formatCurrency(totals.incurred)}
                  </span>
                </span>
                <span>
                  Paid{" "}
                  <span className="font-medium text-foreground">{formatCurrency(totals.paid)}</span>
                </span>
                <span>
                  Reserve{" "}
                  <span className="font-medium text-foreground">
                    {formatCurrency(totals.reserve)}
                  </span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-b-md border border-t-0 bg-card">
        {isLoading && (
          <div className="space-y-2 p-3">
            {["sk-0", "sk-1", "sk-2", "sk-3", "sk-4"].map((k) => (
              <div key={k} className="h-10 animate-pulse rounded bg-muted/40" />
            ))}
          </div>
        )}

        {isError && (
          <div className="p-8 text-center">
            <p className="text-destructive">Failed to load claims.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !isError && allClaims.length === 0 && (
          <div className="p-8 text-center">
            <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-muted-foreground">
              {filters.policyNumber
                ? `No claims yet for policy ${filters.policyNumber}.`
                : filters.agentNumber
                  ? `No claims for agency ${filters.agentNumber}.`
                  : "No claims match these filters."}
            </p>
          </div>
        )}

        {!isLoading && !isError && allClaims.length > 0 && visibleClaims.length === 0 && (
          <div className="p-8 text-center">
            <Search className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-muted-foreground">No claims match "{debouncedFilter}".</p>
          </div>
        )}

        {visibleClaims.length > 0 && (
          // horizontalScroll=false unwraps the table from its overflow-x-auto
          // div so the sticky <thead> can pin to <main> instead of the wrapper
          // (which is the cause of the headers drifting mid-page).
          <Table horizontalScroll={false}>
            {/* Column headers stick at the bottom edge of the upper region.
                `top` is set inline from the measured upper height so the
                pin position stays correct when the upper bar resizes
                (back button toggling, filter chips wrapping). */}
            <TableHeader
              style={{ top: `${headerTop}px` }}
              className="sticky z-20 bg-card shadow-[inset_0_-1px_0_var(--border)]"
            >
              <TableRow>
                <SortableHeader
                  label="Claim Number"
                  sortKey="claimNumber"
                  activeKey={sortKey}
                  activeDir={sortDir}
                  onSort={handleSort}
                  className="sticky left-0 z-30 bg-card"
                />
                {visibleSecondaryKeys.map((key) => {
                  switch (key) {
                    case "claimant":
                      return (
                        <SortableHeader
                          key={key}
                          label="Claimant"
                          sortKey="claimant"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                        />
                      );
                    case "currentStatus":
                      return (
                        <SortableHeader
                          key={key}
                          label="Status"
                          sortKey="currentStatus"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          filter={
                            <MultiSelectColumnFilter
                              ariaLabel="Filter by status"
                              options={statusOptions}
                              value={statusFilter}
                              onChange={setStatusFilter}
                            />
                          }
                        />
                      );
                    case "lossTypeDescription":
                      return (
                        <SortableHeader
                          key={key}
                          label="Loss Type"
                          sortKey="lossTypeDescription"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          filter={
                            <MultiSelectColumnFilter
                              ariaLabel="Filter by loss type"
                              options={lossTypeOptions}
                              value={lossTypeFilter}
                              onChange={setLossTypeFilter}
                            />
                          }
                        />
                      );
                    case "lossDate":
                      return (
                        <SortableHeader
                          key={key}
                          label="Loss Date"
                          sortKey="lossDate"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          filter={
                            <DateRangeColumnFilter
                              ariaLabel="Filter by loss date range"
                              value={lossDateRange}
                              onChange={setLossDateRange}
                            />
                          }
                        />
                      );
                    case "accidentState":
                      return (
                        <SortableHeader
                          key={key}
                          label="State"
                          sortKey="accidentState"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          filter={
                            <MultiSelectColumnFilter
                              ariaLabel="Filter by state"
                              options={stateOptions}
                              value={stateFilter}
                              onChange={setStateFilter}
                              searchable
                            />
                          }
                        />
                      );
                    case "adjusterName":
                      return (
                        <SortableHeader
                          key={key}
                          label="Adjuster"
                          sortKey="adjusterName"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          filter={
                            <MultiSelectColumnFilter
                              ariaLabel="Filter by adjuster"
                              options={adjusterOptions}
                              value={adjusterFilter}
                              onChange={setAdjusterFilter}
                              searchable
                            />
                          }
                        />
                      );
                    case "incurred":
                      return (
                        <SortableHeader
                          key={key}
                          label="Incurred"
                          sortKey="incurred"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          align="right"
                        />
                      );
                    case "reserve":
                      return (
                        <SortableHeader
                          key={key}
                          label="Reserve"
                          sortKey="reserve"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          align="right"
                        />
                      );
                    case "paid":
                      return (
                        <SortableHeader
                          key={key}
                          label="Paid"
                          sortKey="paid"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          align="right"
                        />
                      );
                    default:
                      return null;
                  }
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleClaims.map((c) => {
                const claimantName = c.isRestricted ? "Restricted" : claimantOf(c) || "—";
                // Forward parent context so the Claim Dashboard breadcrumb shows
                // a clickable chain back to the agency/policy the user drilled in
                // from. Filter scope decides which parent to carry.
                const fromHint =
                  filters.policyNumber && !filters.agentNumber
                    ? encodeFromPolicy(filters.policyNumber)
                    : filters.agentNumber && !filters.policyNumber
                      ? encodeFromAgency(filters.agentNumber)
                      : undefined;
                const go = () =>
                  navigate({
                    to: "/claims/$claimNumber",
                    params: { claimNumber: c.claimNumber },
                    search: fromHint ? { from: fromHint } : {},
                  });
                const statusCell = c.currentStatus ? (
                  <StatusBadge
                    statusKey={claimStatusKey(c.currentStatus)}
                    label={c.currentStatus}
                  />
                ) : (
                  <span className="text-muted-foreground">—</span>
                );
                const renderSecondaryCell = (key: SecondaryColumnKey) => {
                  switch (key) {
                    case "claimant":
                      return (
                        <TableCell key={key} className="text-muted-foreground">
                          {claimantName}
                        </TableCell>
                      );
                    case "currentStatus":
                      return <TableCell key={key}>{statusCell}</TableCell>;
                    case "lossTypeDescription":
                      return <TableCell key={key}>{c.lossTypeDescription ?? "—"}</TableCell>;
                    case "lossDate":
                      return (
                        <TableCell key={key}>{c.lossDate ? formatDate(c.lossDate) : "—"}</TableCell>
                      );
                    case "accidentState":
                      return <TableCell key={key}>{c.accidentState ?? "—"}</TableCell>;
                    case "adjusterName":
                      return <TableCell key={key}>{c.adjusterName ?? "—"}</TableCell>;
                    // Dollar columns are visible on restricted rows too —
                    // financial rollups aren't personally identifying, matching
                    // the totals-strip rule and the masking done in the query.
                    case "incurred":
                      return (
                        <TableCell key={key} className="text-right tabular-nums">
                          {formatCurrency(c.incurred)}
                        </TableCell>
                      );
                    case "reserve":
                      return (
                        <TableCell key={key} className="text-right tabular-nums">
                          {formatCurrency(c.reserve)}
                        </TableCell>
                      );
                    case "paid":
                      return (
                        <TableCell key={key} className="text-right tabular-nums">
                          {formatCurrency(c.paid)}
                        </TableCell>
                      );
                    default:
                      return null;
                  }
                };

                if (c.isRestricted) {
                  return (
                    <TableRow
                      key={c.rowId}
                      className="hover:bg-transparent"
                      title="You don't have access to this claim."
                    >
                      <TableCell className="sticky left-0 z-10 bg-card">
                        <StatusBadge
                          statusKey="restricted"
                          label="Restricted"
                          withDot={false}
                          className="gap-1"
                        >
                          <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
                        </StatusBadge>
                      </TableCell>
                      {visibleSecondaryKeys.map(renderSecondaryCell)}
                    </TableRow>
                  );
                }
                return (
                  <TableRow
                    key={c.rowId}
                    className="cursor-pointer"
                    onClick={go}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        go();
                      }
                    }}
                    tabIndex={0}
                  >
                    <TableCell className="sticky left-0 z-10 bg-card font-medium text-primary">
                      <NumberHoverCard
                        preview={{
                          kind: "claim",
                          number: c.claimNumber,
                          isRestricted: c.isRestricted,
                          claimantName: claimantOf(c) || null,
                          status: c.currentStatus,
                          lossDate: c.lossDate,
                          lossType: c.lossTypeDescription,
                        }}
                        copyLabel={`claim ${c.claimNumber}`}
                      >
                        <span>{c.claimNumber}</span>
                      </NumberHoverCard>
                    </TableCell>
                    {visibleSecondaryKeys.map(renderSecondaryCell)}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* The list auto-loads every page (see the drain effect above) so the
            filter menus + refinement cover the whole result. While that's in
            flight, show a passive indicator instead of a manual "Load more"
            button — the totals strip already shows the true count up front. */}
        {allClaims.length > 0 && hasNextPage && (
          <div className="flex justify-center border-t p-4 text-muted-foreground text-sm">
            Loading all claims…
          </div>
        )}
      </div>
    </div>
  );
}
